import React, { useCallback, useMemo, useRef, useState } from "react";
import { FieldArray, FieldArrayRenderProps, Formik } from "formik";
import classnames from "classnames";
import { useDispatch } from "react-redux";
import {
  AccessControl,
  AsyncSelectPickerInput,
  Button,
  DatePickerInput,
  ErrorMessageField,
  Icon,
  Input,
  SelectButton,
  SelectPickerInput,
} from "@shared/components";
import { actions as sharedActions } from "@shared/store";
import { DATE_FORMAT, MODAL_TYPES, PERMISSION } from "@shared/constants";
import { Hospital, SensorDataFile, SensorDataSession, SensorDataSessionMarkerDot } from "@shared/models";
import { OptionWithData } from "@shared/interfaces";
import { getTimezoneList } from "@shared/utils";

import { FilesErrorMessage } from "./components";
import {
  createValidationSchemaSchema,
  FILES_UPLOAD_ACCEPT,
  getHandlers,
  getInitValues,
  prepareCreateSubmitValues,
  prepareUpdateSubmitValues,
} from "./formHelpers";

import "./index.scss";

interface SensorDataSessionFormProps {
  session?: SensorDataSession;
  hospital: Hospital;
  savedDots?: SensorDataSessionMarkerDot[];
  onCloseForm: () => void;
  onChangeForm: () => void;
  onCreateForm: (values: FormData) => void;
  onUpdateForm?: (values: FormData) => void;
  onDelete: (id: number) => void;
  loading: boolean;
}

const SensorDataSessionForm: React.FunctionComponent<SensorDataSessionFormProps> = (props) => {
  const { onCloseForm, onCreateForm, onUpdateForm, session, loading, onDelete, onChangeForm, hospital } = props;

  const dispatch = useDispatch();
  const filesRef = useRef<HTMLInputElement | null>(null);
  const formValues = useMemo(() => getInitValues(session || null, hospital.id), [session, hospital.id]);
  const handlers = useMemo(() => getHandlers(hospital), [hospital]);

  const [isOpenedSelect, setIsOpenedSelect] = useState(false);

  const timezoneOptions = useMemo(() => getTimezoneList(), []);

  const onUploadFiles = useCallback((fileList: FileList | null, arrayHelpers: FieldArrayRenderProps) => {
    Array.from(fileList || []).forEach((f, i) => {
      arrayHelpers.push({
        key: Date.now() + i,
        file: f,
        custom_name: f.name,
        original_name: f.name,
      });
    });

    setIsOpenedSelect(false);
    if (filesRef && filesRef.current) {
      filesRef.current.value = "";
      filesRef.current.files = null;
    }
  }, []);

  const removeFile = useCallback(
    (remove: (i: number) => void, index: number) => {
      if (!session) {
        return remove(index);
      }

      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "Delete File",
            content:
              "Are you sure you want to delete the file? If there’re any dots added to that file, they will be deleted as well.",
            cancelBtnText: "Cancel",
            successBtnText: "Delete",
            onSuccess: () => {
              remove(index);
              dispatch(sharedActions.hideModal());
            },
          },
        }),
      );
    },
    [dispatch, session],
  );

  const selectFiles = useCallback(
    (arrayHelpers: FieldArrayRenderProps) => {
      setIsOpenedSelect(false);
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.SELECT_FILE_FORM,
          props: {
            onSubmit: (values: OptionWithData<SensorDataFile>[]) => {
              values.forEach((f, i) => {
                arrayHelpers.push({
                  id: f.data.id,
                  key: Date.now() + i,
                  custom_name: f.data.custom_name,
                  original_name: f.data.name,
                  is_selected: true,
                });
              });

              dispatch(sharedActions.hideModal());
            },
          },
        }),
      );
    },
    [dispatch],
  );

  return (
    <Formik
      validationSchema={createValidationSchemaSchema}
      validate={() => onChangeForm()}
      enableReinitialize={true}
      validateOnBlur={false}
      onSubmit={(values, { setSubmitting }) => {
        if (!session) onCreateForm(prepareCreateSubmitValues(values));
        if (session && onUpdateForm) onUpdateForm(prepareUpdateSubmitValues(values));

        setSubmitting(false);
      }}
      initialValues={formValues}
    >
      {({ values, handleChange, handleSubmit, handleBlur, touched, errors, setFieldValue }) => (
        <div className="session-form">
          <form onSubmit={handleSubmit}>
            <div className="session-form-header">
              <div className="session-form-header-left">{session ? "Edit Session" : "New Session"}</div>
              <div className="session-form-header-right">
                {!loading ? (
                  <>
                    <AccessControl permission={PERMISSION.CREATE_SENSOR_DATA_SESSION}>
                      <Button type="submit" className="action-btn">
                        {session ? "Save" : "Create"}
                      </Button>
                    </AccessControl>
                    <Button className="close-btn" onClick={() => onCloseForm()}>
                      <Icon type="clear" />
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
            <div className="session-form-content">
              <div className="session-name-row">
                <Input
                  disabled={loading}
                  type="text"
                  labelTitle="Session Name*"
                  labelClassName="session-name-label"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  placeholder="Session Name"
                />
                <ErrorMessageField name="name" />
              </div>
              <div className="session-files-row">
                <div className="session-files-label">Files</div>
                <FieldArray
                  name="files"
                  render={(arrayHelpers) => (
                    <div>
                      {values.files.map((f, i) => (
                        <div className="file-row" key={`${f.key}`}>
                          <div className="file-row-original">
                            <div className="name">{f.original_name}</div>
                            {!loading ? (
                              <div className="action" onClick={() => removeFile(arrayHelpers.remove, i)}>
                                <Icon type="clear" />
                              </div>
                            ) : null}
                          </div>
                          <div>
                            <Input
                              disabled={loading}
                              type="text"
                              labelClassName="file-name-label"
                              labelTitle="File Name*"
                              name={`files.${i}.custom_name`}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.files[i].custom_name}
                              placeholder="File Name"
                            />
                            <ErrorMessageField name={`files.${i}.custom_name`} />
                          </div>
                        </div>
                      ))}
                      {!loading ? (
                        <div className={classnames("add-files-row", { top: !values.files.length })}>
                          <SelectButton
                            text="+ Add File"
                            isOpened={isOpenedSelect}
                            onCloseOpenSelect={setIsOpenedSelect}
                          >
                            <div className="item-file">
                              <label htmlFor="files">Add New</label>
                              <input
                                ref={filesRef}
                                type="file"
                                id="files"
                                accept={FILES_UPLOAD_ACCEPT}
                                multiple
                                onChange={(e) => onUploadFiles(e.currentTarget.files, arrayHelpers)}
                              />
                            </div>
                            <div className="item-file" onClick={() => selectFiles(arrayHelpers)}>
                              Select Existing
                            </div>
                          </SelectButton>
                        </div>
                      ) : null}
                      <div className="add-files-error-row">
                        <FilesErrorMessage name="files" touced={touched} errors={errors} />
                      </div>
                    </div>
                  )}
                />
              </div>
              <div className="session-station-row">
                <div className="session-station-select">
                  <AsyncSelectPickerInput
                    labelTitle="SDH"
                    name="station"
                    className="select-picker"
                    onChange={(v) => setFieldValue("station", v)}
                    value={values.station}
                    placeholder="Select SDH"
                    getData={handlers.station.getData}
                    selectData={handlers.station.selectData}
                    loadingAction={handlers.station.loadingAction}
                    prepareOptionFunction={handlers.station.prepareOptionFunction}
                  />
                  <ErrorMessageField name="station" />
                </div>
                <div className="session-station-timezone">
                  <SelectPickerInput
                    searchable
                    name="station_timezone"
                    data={timezoneOptions}
                    onChange={(value) => setFieldValue("station_timezone", value)}
                    onClean={handleChange}
                    labelTitle="Time Zone"
                    value={values.station_timezone}
                    placeholder="Time Zone"
                    placement="auto"
                  />
                  <ErrorMessageField name="station_timezone" />
                </div>
                <div className="session-station-date-range">
                  <div className="session-station-date-range-item">
                    <DatePickerInput
                      placement="auto"
                      labelTitle="Start"
                      onChange={(e) => setFieldValue("station_start_date", e)}
                      name="station_start_date"
                      value={values.station_start_date}
                      format={DATE_FORMAT.DATE_PICKER}
                    />
                    <ErrorMessageField name="station_start_date" />
                  </div>
                  <div className="session-station-date-range-item">
                    <DatePickerInput
                      placement="auto"
                      labelTitle="End"
                      onChange={(e) => setFieldValue("station_end_date", e)}
                      value={values.station_end_date}
                      name="station_end_date"
                      format={DATE_FORMAT.DATE_PICKER}
                    />
                    <ErrorMessageField name="station_end_date" />
                  </div>
                </div>
              </div>
            </div>
          </form>
          {session ? (
            <AccessControl permission={PERMISSION.DELETE_PATIENT}>
              {session ? (
                <div className="session-form-footer">
                  <Button className="deactivate" onClick={() => onDelete(session.id)}>
                    Delete
                  </Button>
                </div>
              ) : null}
            </AccessControl>
          ) : null}
        </div>
      )}
    </Formik>
  );
};

export default SensorDataSessionForm;
